import { zodResolver } from '@hookform/resolvers/zod'
import { type CreateTypePropertyInput, CreateTypePropertyInputSchema } from '@idc-forms/schemas'
import { Controller, useForm } from 'react-hook-form'
import { useProjectsActiveQuery } from '@/features/projects/hooks/use-projects-query'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import { Button } from '@/shared/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'

type TypePropertyFormProps = {
  /** Valores iniciales (edición) — ausente en creación. */
  defaultValues?: Partial<CreateTypePropertyInput>
  submitLabel: string
  pending?: boolean
  onSubmit: (values: CreateTypePropertyInput) => void
  onCancel: () => void
}

/**
 * Formulario de subtipo (crear/editar) para el side panel. RHF + zodResolver
 * con el MISMO schema (@idc-forms/schemas) que valida el backend. El código
 * es único por proyecto (el backend lo verifica sin distinguir mayúsculas).
 */
export function TypePropertyForm({
  defaultValues,
  submitLabel,
  pending = false,
  onSubmit,
  onCancel
}: TypePropertyFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<CreateTypePropertyInput>({
    resolver: zodResolver(CreateTypePropertyInputSchema),
    defaultValues: {
      active: true,
      ...defaultValues
    }
  })

  const { data: projects = [] } = useProjectsActiveQuery()

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 px-3 py-3" noValidate>
      <FieldGroup className="gap-4">
        <Controller
          control={control}
          name="project"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.project)}>
              <FieldLabel htmlFor="type-property-project">Proyecto</FieldLabel>
              <NativeSelect
                id="type-property-project"
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value)
                }}
              >
                <NativeSelectOption value="" disabled>
                  Selecciona un proyecto
                </NativeSelectOption>
                {projects.map((project) => (
                  <NativeSelectOption key={project._id} value={project._id}>
                    {project.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <FieldError errors={[errors.project]} />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.code)}>
              <FieldLabel htmlFor="type-property-code">Código</FieldLabel>
              <Input
                id="type-property-code"
                value={field.value ?? ''}
                onChange={field.onChange}
                autoComplete="off"
              />
              <FieldDescription>
                Único dentro del proyecto (sin distinguir mayúsculas). Es el prefijo de los códigos
                de inmueble.
              </FieldDescription>
              <FieldError errors={[errors.code]} />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.name)}>
              <FieldLabel htmlFor="type-property-name">Nombre</FieldLabel>
              <Input
                id="type-property-name"
                value={field.value ?? ''}
                onChange={field.onChange}
                autoComplete="off"
              />
              <FieldDescription>Opcional: etiqueta descriptiva del subtipo.</FieldDescription>
              <FieldError errors={[errors.name]} />
            </Field>
          )}
        />

        <Controller
          control={control}
          name="active"
          render={({ field }) => (
            <Field orientation="horizontal">
              <FieldLabel htmlFor="type-property-active">Activo</FieldLabel>
              <Switch
                id="type-property-active"
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
              />
            </Field>
          )}
        />
      </FieldGroup>

      <SidePanelActions
        actions={[
          {
            label: submitLabel,
            variant: 'default',
            disabled: pending,
            primary: true,
            onClick: () => {
              void handleSubmit(onSubmit)()
            }
          },
          {
            label: 'Cancelar',
            variant: 'outline',
            disabled: pending,
            onClick: onCancel
          }
        ]}
      />
      {/* El submit real va por SidePanelActions; este botón invisible habilita Enter. */}
      <Button type="submit" className="hidden" disabled={pending}>
        {submitLabel}
      </Button>
    </form>
  )
}
